import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Radio,
  Typography,
  useTheme,
  Checkbox
} from "@mui/material";

import { Stack } from "@mui/system";
import { Play20Px } from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { flaggedReasonIconMap, flaggedReasons } from "~/lib/bin";
import { capitalizeFirstLetter } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelModalName } from "~/lib/mixpanel-tracking";
import { parseProductTempZone, tempZoneIconMap } from "~/lib/tempZone";

import { setMaintenanceConfig } from "./binMaintenanceWorkstation.slice";

type StartMaintenanceConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

/**
 * Modal for configuring tempzone and bin type for bin maintenance
 */
export function StartMaintenanceConfigModal({
  isOpen,
  onClose
}: StartMaintenanceConfigModalProps) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const trackedModalName = useMixpanelModalName("Start Bin Maintenance");

  const { tempZones } = useGetMultiTempGridInfo();
  const { tempZone: selectedTempZone, flaggedReasons: selectedFlaggedReasons } =
    useAppSelector(
      (state) => state.binMaintenanceWorkstation.maintenanceConfig
    );

  return (
    <Dialog open={isOpen} maxWidth="sm" onClose={onClose}>
      <DialogTitle>{t("start bin maintenance")}</DialogTitle>

      <DialogContent sx={{ px: "60px" }}>
        <Typography
          sx={{
            textAlign: "center",
            fontSize: "1.25rem",
            my: 2.5,
            mx: "auto",
            color: "text.secondary"
          }}
        >
          {t("configure your maintenance session below")}
        </Typography>
        <Typography
          variant="h6"
          sx={{ textAlign: "center", width: "100%", fontWeight: 400 }}
        >
          {t("temperature zone")}
        </Typography>
        <Stack spacing={1.5}>
          {tempZones.map((zone) => {
            const normalizedZone = parseProductTempZone(zone);
            if (!normalizedZone) return null;
            const Icon = tempZoneIconMap[normalizedZone];
            const isSelected = selectedTempZone === zone;
            const zoneColor = palette[normalizedZone].main;

            return (
              <Box
                key={zone}
                role="button"
                tabIndex={0}
                onClick={() => {
                  dispatch(setMaintenanceConfig({ tempZone: zone }));
                  mixpanelTrack({
                    type: "Select Option",
                    label: "Select Temp Zone",
                    trackedPageName: trackedModalName,
                    eventProperties: { "Temp Zone": zone }
                  });
                }}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  px: 1.5,
                  py: 1.5,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  border: 3,
                  borderColor: isSelected ? "primary.main" : "grey.300",
                  cursor: "pointer"
                }}
              >
                <Radio
                  checked={isSelected}
                  sx={{
                    "& .MuiSvgIcon-root": {
                      fontSize: "35px"
                    }
                  }}
                  slotProps={{
                    input: {
                      "aria-label": `${zone} temperature zone`
                    }
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    bgcolor: zoneColor,
                    borderRadius: 1,
                    px: 2.5,
                    py: 1
                  }}
                >
                  <Typography sx={{ fontSize: "1.5rem" }}>
                    {capitalizeFirstLetter(zone.toLowerCase())}
                  </Typography>
                  {Icon && <Icon width={24} height={24} />}
                </Box>
              </Box>
            );
          })}
        </Stack>

        <Box my={2} mb={2}>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", width: "100%", fontWeight: 400 }}
          >
            {t("flag type")}
          </Typography>
          <Stack spacing={1.5}>
            {flaggedReasons.map((reason) => {
              const isSelected = selectedFlaggedReasons.includes(reason);
              const Icon = flaggedReasonIconMap[reason];
              return (
                <Box
                  key={reason}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    const nextFlaggedReasons = selectedFlaggedReasons.includes(
                      reason
                    )
                      ? selectedFlaggedReasons.filter((r) => r !== reason)
                      : [...selectedFlaggedReasons, reason];
                    dispatch(
                      setMaintenanceConfig({
                        flaggedReasons: nextFlaggedReasons
                      })
                    );
                  }}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.5,
                    borderRadius: 1,
                    bgcolor: "background.paper",
                    border: 3,
                    borderColor: isSelected ? "primary.main" : "grey.300",
                    cursor: "pointer"
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: "35px"
                      }
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      bgcolor: palette.background.gray,
                      borderRadius: 1,
                      px: 2.5,
                      py: 1
                    }}
                  >
                    <Typography sx={{ fontSize: "1.5rem" }}>
                      {capitalizeFirstLetter(reason.toLowerCase())}
                    </Typography>
                    {Icon && <Icon width={24} height={24} />}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pt: 2 }}>
        <Button
          size="large"
          variant="contained"
          color="secondary"
          startIcon={<KeyboardBackspaceIcon />}
          onClick={() => {
            mixpanelTrack({
              type: "Button Click",
              label: "Back",
              trackedPageName: trackedModalName
            });
            onClose();
          }}
          sx={{ py: 1.5, px: 4, minWidth: "140px" }}
        >
          {t("back")}
        </Button>
        <Button
          size="large"
          variant="contained"
          color="primary"
          startIcon={<Play20Px fill="white" />}
          disabled={!selectedTempZone || !selectedFlaggedReasons.length}
          onClick={() => {
            if (selectedTempZone) {
              mixpanelTrack({
                type: "Button Click",
                label: "Start",
                trackedPageName: trackedModalName,
                eventProperties: {
                  "Temp Zone": selectedTempZone
                }
              });
              dispatch(
                setMaintenanceConfig({
                  tempZone: selectedTempZone,
                  flaggedReasons: selectedFlaggedReasons
                })
              );
              onClose();
            }
          }}
          sx={{ py: 1.5, px: 4, minWidth: "140px" }}
        >
          {t("start")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
